import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import DriverNav from './DriverNav';
import { HiUser, HiExclamationCircle, HiLogout, HiLockClosed } from 'react-icons/hi';

const Field = ({ label, value }) => (
    <div className="py-3 border-b border-gray-50 last:border-0">
        <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-800">{value || 'N/A'}</p>
    </div>
);

const DriverProfile = () => {
    const { user, logout } = useAuth();
    const driverId = user?.linked_entity_id;
    const [driver, setDriver] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [pwLoading, setPwLoading] = useState(false);
    const [pwError, setPwError] = useState(null);
    const [pwSuccess, setPwSuccess] = useState(false);

    useEffect(() => {
        if (!driverId) return;
        axios.get(`${rootUrl}/drivers/${driverId}`)
            .then(res => setDriver(res.data))
            .catch(() => setError('Failed to load profile.'))
            .finally(() => setLoading(false));
    }, [driverId]);

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPwError(null);
        setPwSuccess(false);
        if (pwForm.newPassword !== pwForm.confirmPassword) {
            setPwError('New passwords do not match.');
            return;
        }
        setPwLoading(true);
        try {
            await axios.post(`${rootUrl}/api/users/profile/change-password`, pwForm);
            setPwSuccess(true);
            setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            setPwError(err.response?.data?.message || 'Failed to change password.');
        } finally {
            setPwLoading(false);
        }
    };

    if (!driverId) return (
        <div className="min-h-screen flex items-center justify-center text-gray-500 pb-16">
            No driver account linked.
        </div>
    );

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center pb-16">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="bg-orange-500 px-4 pt-10 pb-10 flex flex-col items-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-3 shadow">
                    <HiUser className="w-8 h-8 text-orange-500" />
                </div>
                <h1 className="text-white text-xl font-bold">{driver?.name || user?.name}</h1>
                <p className="text-orange-100 text-sm mt-0.5">{user?.username}</p>
            </div>

            <div className="px-4 py-4 space-y-4">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-600 text-sm">
                        <HiExclamationCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-sm px-4">
                    <Field label="Full Name" value={driver?.name} />
                    <Field label="ID Number" value={driver?.id_no} />
                    <Field label="Phone" value={driver?.phone} />
                    <Field label="Email" value={driver?.email || user?.email} />
                    <Field label="License Number" value={driver?.license_no} />
                    <Field label="Status" value={driver?.status} />
                </div>

                <div className="bg-white rounded-2xl shadow-sm px-4">
                    <Field label="Username" value={user?.username} />
                    <Field label="Account Email" value={user?.email} />
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-4">
                    <div className="flex items-center gap-2 mb-4">
                        <HiLockClosed className="w-5 h-5 text-orange-500" />
                        <h2 className="text-sm font-semibold text-gray-700">Change Password</h2>
                    </div>
                    <form onSubmit={handlePasswordChange} className="space-y-3">
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Current Password</label>
                            <input
                                type="password"
                                value={pwForm.currentPassword}
                                onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))}
                                required
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">New Password</label>
                            <input
                                type="password"
                                value={pwForm.newPassword}
                                onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))}
                                required
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 block mb-1">Confirm New Password</label>
                            <input
                                type="password"
                                value={pwForm.confirmPassword}
                                onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))}
                                required
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                            />
                        </div>
                        {pwError && (
                            <div className="flex items-center gap-2 text-red-500 text-xs">
                                <HiExclamationCircle className="w-4 h-4 flex-shrink-0" />
                                {pwError}
                            </div>
                        )}
                        {pwSuccess && (
                            <p className="text-green-600 text-xs font-medium">Password changed successfully.</p>
                        )}
                        <button
                            type="submit"
                            disabled={pwLoading}
                            className="w-full bg-orange-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50"
                        >
                            {pwLoading ? 'Saving…' : 'Update Password'}
                        </button>
                    </form>
                </div>

                <button
                    onClick={logout}
                    className="w-full flex items-center justify-center gap-2 bg-red-50 text-red-500 border border-red-200 rounded-2xl py-3 text-sm font-semibold hover:bg-red-100 transition"
                >
                    <HiLogout className="w-5 h-5" />
                    Sign Out
                </button>
            </div>

            <DriverNav />
        </div>
    );
};

export default DriverProfile;
